if (!customElements.get('sh-mobile-search')) {
  customElements.define('sh-mobile-search', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.header = this.closest('header');
      this.dialog = this.querySelector('dialog');
      this.trigger = this.header.querySelector('.sh-mobile-search-trigger');
      this.panel = this.header.querySelector('.header__search');
      this.predictive = this.panel.querySelector('predictive-search');
      this.input = this.panel.querySelector('input[type="search"]');
      this.results = this.panel.querySelector('[id^="PredictiveSearchResults-"]');
      this.mobile = window.matchMedia('(max-width: 1023px)');
      this.home = document.createComment('mobile search return position');
      this.panel.before(this.home);

      this.trigger.addEventListener('click', (event) => {
        if (!this.mobile.matches || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.open();
      }, { signal });

      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('.header__search-close')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.dialog.close();
        }
        if (event.target.closest('button[type="reset"]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.input.value = '';
          this.predictive.removeAttribute('results');
          this.results.replaceChildren();
          this.input.focus({ preventScroll: true });
          this.predictive.classList.add('predictive-search-open');
          this.predictive.searchContent?.classList.toggle('hidden', this.predictive.searchRecommendationEmpty);
          this.syncAccessibility();
        }
      }, { capture: true, signal });

      this.dialog.addEventListener('close', () => this.close(), { signal });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.dialog.close();
        }
        if (event.key === 'Tab') {
          const controls = [...this.dialog.querySelectorAll('a[href], button, input:not([type="hidden"]), select, [tabindex]')]
            .filter((element) => !element.disabled && element.tabIndex >= 0 &&
              element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus({ preventScroll: true });
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus({ preventScroll: true });
          }
        }
      }, { capture: true, signal });
      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches) {
          if (this.dialog.open) this.dialog.close();
          this.restore();
        }
      }, { signal });
      window.visualViewport?.addEventListener('resize', () => this.size(), { signal });
      window.addEventListener('pageshow', () => {
        if (this.dialog.open) this.dialog.close();
      }, { signal });

      this.observer = new MutationObserver(() => {
        if (!this.mobile.matches || !this.dialog.contains(this.panel)) return;
        if (!this.dialog.open) {
          if (this.predictive.classList.contains('predictive-search-open')) {
            this.predictive.classList.remove('predictive-search-open');
          }
          if (document.body.classList.contains('search-open')) document.body.classList.remove('search-open');
        }
        if (!this.input.value.trim()) {
          if (this.predictive.hasAttribute('results')) this.predictive.removeAttribute('results');
          if (this.results.childElementCount) this.results.replaceChildren();
        }
        this.syncAccessibility();
      });
      this.observer.observe(this.predictive, { attributes: true, attributeFilter: ['class', 'results'] });
      this.observer.observe(this.results, { childList: true });
    }

    open() {
      if (this.dialog.open) return;
      this.scrollPosition = window.scrollY;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.append(this.panel);
      this.size();
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('sh-mobile-search-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.input.focus({ preventScroll: true });
      this.predictive.classList.add('predictive-search-open');
      this.syncAccessibility();
    }

    size() {
      if (!this.mobile.matches) return;
      this.dialog.style.setProperty('--sh-search-viewport-height',
        `${Math.round(window.visualViewport?.height || window.innerHeight)}px`);
    }

    syncAccessibility() {
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-owns', this.results.id);
      this.input.setAttribute('aria-expanded',
        String(this.dialog.open && !!this.results.querySelector('a, button') && this.input.value.trim().length > 0));
    }

    close() {
      this.predictive.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open', 'sh-mobile-search-open');
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.trigger.setAttribute('aria-expanded', 'false');
      this.syncAccessibility();
      this.input.blur();
      window.scrollTo({ top: this.scrollPosition || 0, behavior: 'instant' });
      if (this.mobile.matches) this.trigger.focus({ preventScroll: true });
    }

    restore() {
      this.home.after(this.panel);
      this.input.setAttribute('aria-expanded', 'false');
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.dialog?.open) {
        this.dialog.close();
        this.close();
      }
      this.controller?.abort();
      this.controller = null;
    }
  });
}
